<template>
  <div :id="`${source}_video_attachment_display`" class="max-w-[300px] w-full">
    <div
      class="relative rounded-xl overflow-hidden cursor-pointer bg_8 min-h-[150px] group"
      :class="{ 'rounded-t-xl rounded-b-none': props.caption }"
      @click="showPreview"
    >
      <video
        ref="videoRef"
        class="w-full h-auto max-h-[300px] object-cover block rounded-xl"
        :class="{ 'rounded-t-xl rounded-b-none': props.caption }"
        :src="attachment.url"
        preload="metadata"
        @loadedmetadata="onVideoLoad"
        @error="onVideoError"
      >
        <source
          :src="attachment.url"
          :type="attachment.content_type || 'video/mp4'"
        />
      </video>

      <div v-if="videoLoading" class="absolute inset-0 flex items-center justify-center bg_8 rounded-xl">
        <CustomLoader :size="24" />
      </div>

      <div v-if="videoError" class="absolute inset-0 flex items-center justify-center bg_8 rounded-xl">
        <ThemeImage
          :icon="imageNames.VIDEO_GREY"
          class="size-8 opacity-50"
          alt="video_error"
        />
      </div>

      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div class="flex items-center justify-center size-14 rounded-full bg-black/60 backdrop-blur-sm transition-all group-hover:scale-110 group-hover:bg-black/80">
          <ThemeImage
            :icon="imageNames.PLAY_VARIANT1_ICON"
            class="size-6"
            alt="play_icon"
          />
        </div>
      </div>

      <div v-if="videoDuration" class="absolute bottom-2 left-2 px-1.5 py-0.5 rounded text-white text-xs font-medium bg-black/60">
        {{ formatDuration(videoDuration) }}
      </div>

      <div class="media_overlay absolute inset-0 flex items-end justify-end p-2 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity">
        <div class="flex gap-1.5">
          <div
            class="flex items-center justify-center size-8 rounded-full cursor-pointer bg-black/50 backdrop-blur-sm transition-all hover:bg-black/70 hover:scale-110"
            @click.stop="downloadVideo"
            title="Download"
          >
            <ThemeImage
              :icon="imageNames.DOWNLOAD_GREY"
              class="size-4"
              alt="download"
            />
          </div>
        </div>
      </div>
      <div v-if="props.caption" class="bg_8 py-2 px-3 text-sm leading-[1.4] text_color rounded-b-xl break-words">
        {{ props.caption }}
      </div>
    </div>

    <VideoPreview
      :source="source"
      :visible="previewVisible"
      :video="{ url: attachment.url }"
      @update:visible="previewVisible = $event"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import VideoPreview from "@/src/components/preview_components/VideoPreview.vue";
import { downloadAttachment } from "@/src/utils/chatUtils.js";

const props = defineProps({
  source: { type: String, required: true },
  attachment: { type: Object, required: true },
  caption: { type: String, default: "" },
});

const previewVisible = ref(false);
const videoRef = ref(null);
const videoLoading = ref(true);
const videoError = ref(false);
const videoDuration = ref(0);

const showPreview = () => {
  previewVisible.value = true;
};

const onVideoLoad = () => {
  videoLoading.value = false;
  videoError.value = false;
  if (videoRef.value) {
    videoDuration.value = videoRef.value.duration;
  }
};

const onVideoError = () => {
  videoLoading.value = false;
  videoError.value = true;
};

const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return "";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const downloadVideo = async () => {
  if (!props.attachment?.url) return;

  const isChatSource = props.source === 'chat_message' || props.source.includes('chat');

  if (isChatSource) {
    await downloadAttachment(props.attachment, (errorMsg) => {
      console.error("Error downloading video:", errorMsg);
    });
    return;
  }

  try {
    const response = await fetch(props.attachment.url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = props.attachment.file_name || "video.mp4";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading video:", error);
  }
};
</script>

<style scoped>
.media_overlay {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 40%);
}
</style>
